import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import OrderDetails from '../../components/OrderDetails'
import CardForm from '../../components/CardForm';
import AddressForm from '../../components/AddressForm';
import TermsAndSubmit from '../../components/TermsAndSubmit';
import Payments from '../api/payments';
import Pagarme from '../api/pagarme';
import { ROUTES } from '../constants/routes';
import { useUser } from '../../hooks/useUser';
import axios from 'axios';
import LoadingComponent from '../../components/Loading';
import { salesFlowStart, salesFlowConfirm, salesFlowError } from '../../utils/salesFlowLogger';
import { isPaymentFormValid } from '../utils/isPaymentFormValid';

const Payment = () => { 
  const { currentUser, checkAuth } = useUser();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { 
    licensePlate: stateLicensePlate, 
    price: statePrice,
    planType: statePlanType,
    scheduling 
  } = location.state || {};

  const licensePlate = stateLicensePlate ?? '';
  const price = statePrice ?? 0;
  const planType = statePlanType ?? '';

  if (!licensePlate || !price) {
    return <LoadingComponent />;
  }

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [card, setCard] = useState({
    number: '',
    name: '',
    expiry: '',
    cvv: '',
    focus: ''
  });

  const [cardErrors, setCardErrors] = useState({});
  const [addressErrors, setAddressErrors] = useState({});

  const [address, setAddress] = useState({
    zip_code: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });

  const [termsAccepted, setTermsAccepted] = useState(false);

  const cardFormRef = useRef(null);
  const addressFormRef = useRef(null);

  useEffect(() => {
    if (currentUser?.last_billing_address) {
      setAddress({
        zip_code: currentUser.last_billing_address.zip_code || '',
        street: currentUser.last_billing_address.address || '',
        number: currentUser.last_billing_address.number || '',
        complement: currentUser.last_billing_address.complement || '',
        neighborhood: currentUser.last_billing_address.neighborhood || '',
        city: currentUser.last_billing_address.city || '',
        state: currentUser.last_billing_address.state || ''
      });
    }
  }, [currentUser]);

  const handleTermsChange = (event) => {
    setTermsAccepted(event.target.checked);
  };

  const handlePaymentSubmit = async () => {
    try {
      const isCardValid = await cardFormRef.current?.validate();
      const isAddressValid = await addressFormRef.current?.validate();

      if (!isCardValid || !isAddressValid || !termsAccepted) {
        return;
      }

      setIsSubmitting(true);
      salesFlowStart('Payment', 'Pagamento iniciado', { inspection_id: id });

      const currentAddress = currentUser?.last_billing_address;
      const hasAddressChanged = !currentAddress || 
        address.zip_code !== currentAddress.zip_code ||
        address.street !== currentAddress.address ||
        address.number !== currentAddress.number ||
        address.complement !== currentAddress.complement ||
        address.neighborhood !== currentAddress.neighborhood ||
        address.city !== currentAddress.city ||
        address.state !== currentAddress.state;

      if (hasAddressChanged) {
        try {
          await axios.put(ROUTES.UPDATE_USER, {
            user: {
              billing_address: {
                zip_code: address.zip_code,
                address: address.street,
                number: address.number,
                complement: address.complement || '',
                neighborhood: address.neighborhood,
                city: address.city,
                state: address.state
              }
            }
          });
          
          await checkAuth();
          
        } catch (error) {
          salesFlowError('Payment', 'Erro ao atualizar endereço', error);
          setErrorMessage('Erro ao atualizar endereço. Por favor, tente novamente.');
          return;
        }
      }

      let cardToken;
      try {
        cardToken = await Pagarme.createCardToken(card);
      } catch (tokenError) {
        salesFlowError('Payment', 'Erro ao tokenizar cartão', tokenError);
        setErrorMessage('Erro ao processar dados do cartão. Verifique os dados e tente novamente.');
        setIsSubmitting(false);
        return;
      }

      const paymentData = {
        inspection_id: id,
        card_token: cardToken,
        address: {
          zip_code: address.zip_code.replace(/\D/g, ''),
          street: address.street,
          number: address.number,
          complement: address.complement || '',
          neighborhood: address.neighborhood,
          city: address.city,
          state: address.state
        },
        terms_accepted: termsAccepted,
        license_plate: licensePlate,
        price: price,
        scheduling
      };

      const response = await Payments.create(paymentData);
      
      if (response?.payment?.inspection_id) {
        salesFlowConfirm('Payment', 'Pagamento realizado com sucesso', {
          inspection_id: response.payment.inspection_id,
        });
        navigate(`${ROUTES.FINALIZATION}/${response.payment.inspection_id}`);
      } else {
        throw new Error(response.error || 'Erro ao processar pagamento');
      }

    } catch (error) {
      salesFlowError('Payment', 'Erro no pagamento', error);
      const errorMessage = error?.data?.error || error.message || 'Ocorreu um erro ao processar o pagamento';
      setErrorMessage(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (cardFormRef.current && card.number && card.name && card.expiry && card.cvv) {
      cardFormRef.current.validate();
    }
  }, [card]);

  useEffect(() => {
    if (addressFormRef.current && address.zip_code && address.street && address.number) {
      addressFormRef.current.validate();
    }
  }, [address]);

  const isFormValid = () =>
    isPaymentFormValid({
      card,
      address,
      cardErrors,
      addressErrors,
      termsAccepted,
      isSubmitting,
    });

  return (
    <Box sx={{ 
      bgcolor: '#F5F7F9',
      minHeight: '100vh',
      pt: { xs: 8, sm: 9, md: 10 },
      pb: { xs: 4, sm: 5, md: 6 },
      px: { xs: 2, sm: 3, md: 4 },
    }}>
      <Typography 
        variant="h1" 
        sx={{
          textAlign: 'center',
          color: '#012F47',
          fontWeight: 700,
          fontFamily: 'GT Haptik, Raleway, sans-serif',
          fontSize: { xs: '24px', md: '32px' },
          mb: { xs: 3, md: 4 }
        }}
      >
        Pagamento
      </Typography>
      <OrderDetails 
        licensePlate={licensePlate}
        price={price}
        planType={planType}
      />
      <CardForm 
        ref={cardFormRef}
        card={card}
        setCard={setCard}
        errors={cardErrors}
        setErrors={setCardErrors}
      />
      <AddressForm 
        ref={addressFormRef}
        address={address}
        setAddress={setAddress}
        errors={addressErrors}
        setErrors={setAddressErrors}
        currentUser={currentUser}
      />
      <TermsAndSubmit
        checked={termsAccepted}
        onCheckChange={handleTermsChange}
        onSubmit={handlePaymentSubmit}
        disabled={!isFormValid()}
        isLoading={isSubmitting}
      />
      {errorMessage && (
        <Typography variant="body1" color="error" sx={{ mt: 2, textAlign: 'center' }}>
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default Payment;
